/**
 * AppCapacitor - Simple iOS Authentication for Runium
 *
 * Flow:
 * 1. User taps "Sign In" -> Opens Clerk in Safari
 * 2. User authenticates with Clerk
 * 3. Clerk redirects to https://app.runium.ai/auth-callback
 * 4. Callback page redirects to com.runium.app://auth-callback?token=...
 * 5. App receives deep link, stores token, shows Dashboard
 */

import React, { useState, useEffect, useRef } from 'react';
import { Browser } from '@capacitor/browser';
import { App as CapApp } from '@capacitor/app';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { CapacitorAuthProvider } from './utils/CapacitorAuthContext';
import Dashboard from './components/Dashboard';
import { ChatManagerProvider } from './components/ChatManager';
import { SubscriptionProvider } from './contexts/SubscriptionContext';

// Error boundary to catch and display errors in iOS
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 bg-gray-900 text-white p-4 overflow-auto">
          <h1 className="text-xl font-bold text-red-500 mb-4">Something went wrong</h1>
          <div className="bg-gray-800 rounded p-3 mb-4">
            <p className="text-red-400 font-mono text-sm break-all">
              {this.state.error?.toString()}
            </p>
          </div>
          <div className="bg-gray-800 rounded p-3">
            <p className="text-gray-400 font-mono text-xs break-all whitespace-pre-wrap">
              {this.state.errorInfo?.componentStack}
            </p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-blue-500 px-4 py-2 rounded"
          >
            Reload App
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Clerk configuration
const CLERK_ACCOUNT_PORTAL = 'smooth-primate-27.accounts.dev';
const WEB_REDIRECT_URI = 'https://app.runium.ai/auth-callback';
const APP_SCHEME_URI = 'com.runium.app://auth-callback';

// Simple sign-in page
const SignInPage = ({ onSignIn, debugLogs }) => (
  <div className="fixed inset-0 flex flex-col items-center justify-center bg-gray-900 text-white p-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
    <div className="mb-8 text-center">
      <svg className="w-24 h-24 mx-auto mb-5 text-blue-400 filter drop-shadow-[0_0_8px_rgba(59,130,246,0.5)]" viewBox="0.00 0.00 1024.00 1024.00" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M 690.43 147.34 C 712.23 158.92 727.32 179.19 732.81 202.98 C 733.92 207.79 733.96 211.47 734.63 216.10 Q 734.82 217.40 734.75 221.40 Q 733.98 262.15 702.78 288.31 C 683.27 304.66 657.91 310.13 632.96 305.55 C 603.60 300.15 580.68 280.59 569.84 252.90 Q 560.19 228.25 566.46 201.45 Q 571.65 179.27 588.27 162.28 Q 609.04 141.06 640.73 137.00 Q 642.78 136.74 651.57 136.90 Q 657.89 137.01 662.36 137.83 Q 677.94 140.70 690.43 147.34 Z"/>
        <path d="M 600.71 431.49 A 0.39 0.39 0.0 0 0 599.98 431.43 Q 598.53 434.71 595.85 439.37 Q 577.61 471.07 528.88 554.52 A 0.89 0.88 -53.2 0 0 529.03 555.61 Q 531.65 558.11 535.18 561.01 Q 564.45 585.03 622.72 634.51 Q 636.24 645.99 639.58 650.98 Q 647.78 663.26 647.60 678.18 Q 647.52 685.35 644.40 695.38 Q 625.49 756.27 590.89 867.18 C 583.52 890.81 559.07 905.19 535.19 896.49 C 515.56 889.34 503.67 868.27 509.40 847.41 Q 510.54 843.28 512.45 837.16 Q 541.83 743.04 551.68 710.69 Q 555.42 698.40 558.72 688.87 A 0.79 0.78 -61.1 0 0 558.48 688.02 C 511.75 649.33 475.47 618.94 433.58 585.51 C 431.31 583.70 429.21 581.57 426.71 579.19 Q 411.38 564.56 407.44 543.81 Q 407.10 542.06 406.45 536.46 Q 405.94 532.16 406.19 529.04 Q 407.32 514.84 412.95 505.43 Q 455.03 435.10 501.88 355.25 A 0.45 0.45 0.0 0 0 501.61 354.59 L 430.72 335.53 A 0.91 0.91 0.0 0 0 429.90 335.72 C 421.79 342.81 415.34 349.09 407.71 356.07 Q 374.05 386.85 336.32 422.04 C 334.32 423.90 332.28 425.38 330.43 427.03 Q 329.09 428.21 327.94 428.87 Q 317.11 435.07 305.98 433.87 Q 291.81 432.36 283.20 422.47 C 271.24 408.73 270.80 389.09 282.66 375.10 Q 283.94 373.60 288.06 369.55 Q 295.65 362.10 335.91 325.16 Q 366.46 297.13 389.34 275.91 Q 397.82 268.05 401.44 266.04 Q 407.80 262.51 416.25 261.04 Q 418.36 260.67 424.84 260.85 Q 428.43 260.95 433.32 262.06 Q 441.85 263.99 442.00 264.03 Q 485.43 275.16 537.13 288.86 Q 548.24 291.80 568.18 296.70 Q 587.87 301.53 595.00 304.97 Q 618.44 316.24 630.63 339.12 Q 644.12 364.44 687.18 449.07 A 0.73 0.73 0.0 0 0 687.90 449.45 Q 695.94 448.59 704.29 448.19 Q 713.88 447.72 720.75 447.21 Q 749.42 445.09 755.51 444.80 C 757.90 444.69 760.97 444.05 763.51 444.07 C 769.80 444.10 775.56 442.94 781.33 442.93 C 784.72 442.92 789.73 442.05 793.56 442.62 C 809.32 444.98 819.92 455.21 823.82 470.70 Q 824.21 472.23 824.08 479.25 Q 824.00 483.59 822.59 487.62 Q 816.19 505.94 796.96 511.64 Q 793.24 512.74 780.73 513.12 A 13.48 12.95 -23.1 0 0 778.84 513.31 C 774.83 514.00 770.86 513.88 767.16 514.21 Q 757.75 515.05 742.81 515.94 C 737.36 516.26 732.89 517.08 727.35 517.07 C 724.85 517.07 722.49 517.85 719.93 517.89 Q 713.36 518.00 709.15 518.54 C 704.79 519.10 699.95 518.78 695.74 519.53 C 691.56 520.27 688.08 519.85 683.78 520.39 C 675.94 521.37 665.90 521.77 658.73 519.51 Q 644.45 515.01 637.19 501.53 Q 622.99 475.17 601.53 433.86 Q 601.06 432.95 600.71 431.49 Z"/>
        <path d="M 391.27 548.22 C 397.03 578.68 419.25 598.11 443.95 614.55 A 0.68 0.68 0.0 0 1 443.99 615.65 Q 443.80 615.79 443.69 615.93 Q 419.16 648.70 379.29 703.78 Q 378.79 704.47 374.51 709.00 Q 363.45 720.70 347.36 722.76 Q 342.62 723.36 333.75 723.30 Q 314.98 723.17 194.00 723.24 C 189.09 723.24 183.01 722.16 178.25 719.98 Q 161.09 712.13 155.62 694.13 C 149.33 673.44 160.29 650.61 181.53 644.40 Q 186.30 643.00 197.11 643.01 Q 292.37 643.09 324.15 643.02 A 0.56 0.53 16.7 0 0 324.60 642.79 L 390.20 547.99 A 0.60 0.59 56.9 0 1 391.27 548.22 Z"/>
      </svg>
      <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600 mb-2">Welcome to Runium</h1>
      <p className="mt-2 text-gray-300 text-lg">Your personal AI running coach</p>
    </div>

    <div className="w-full max-w-md">
      <button
        onClick={onSignIn}
        className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-4 px-6 rounded-lg transition-colors duration-200 shadow-lg"
      >
        Sign In with Clerk
      </button>
      <p className="mt-4 text-gray-400 text-sm text-center">
        Opens in Safari for secure authentication
      </p>
    </div>

    {/* Debug panel */}
    {debugLogs.length > 0 && (
      <div className="mt-8 w-full max-w-md bg-gray-800 rounded p-3 border border-gray-700 max-h-48 overflow-y-auto">
        <p className="text-xs text-gray-400 mb-2">Debug:</p>
        {debugLogs.slice(-10).map((log, i) => (
          <p key={i} className="text-xs text-gray-300 font-mono">{log}</p>
        ))}
      </div>
    )}
  </div>
);

function AppCapacitor() {
  // Check localStorage for existing session
  const [isSignedIn, setIsSignedIn] = useState(() => {
    const hasToken = !!localStorage.getItem('clerk_token');
    console.log('[AppCapacitor] Initial isSignedIn:', hasToken);
    return hasToken;
  });
  const [token, setToken] = useState(() => localStorage.getItem('clerk_token'));
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('clerk_session_id'));
  const [debugLogs, setDebugLogs] = useState(['App initialized']);
  const listenerSetup = useRef(false);

  // Simple logging
  const log = (msg) => {
    const timestamp = new Date().toLocaleTimeString();
    console.log('[AppCapacitor]', msg);
    setDebugLogs(prev => [...prev.slice(-15), `${timestamp}: ${msg}`]);
  };

  // Setup deep link listener once
  useEffect(() => {
    if (listenerSetup.current) return;
    listenerSetup.current = true;

    log('Setting up deep link listener...');

    const handleDeepLink = async (data) => {
      log(`Deep link received: ${data.url.substring(0, 60)}...`);

      // Check if this is our auth callback
      if (data.url.startsWith(APP_SCHEME_URI) || data.url.includes('/auth-callback')) {
        try {
          await Browser.close();
          log('Browser closed');
        } catch (e) {
          log('Browser close error: ' + e.message);
        }

        // Parse URL - handle both custom scheme and https
        let urlToParse = data.url;
        if (urlToParse.startsWith('com.runium.app://')) {
          urlToParse = urlToParse.replace('com.runium.app://', 'https://app.runium.ai/');
        }

        try {
          const url = new URL(urlToParse);
          const params = new URLSearchParams(url.search);

          // Also check hash fragment
          const hashParams = new URLSearchParams(url.hash.replace('#', ''));

          // Get token from various possible locations
          const receivedToken = params.get('token') || hashParams.get('token');
          const receivedSessionId = params.get('session_id') ||
                                    params.get('__clerk_created_session') ||
                                    hashParams.get('session_id');

          log(`Token: ${receivedToken ? receivedToken.substring(0, 20) + '...' : 'none'}`);
          log(`Session: ${receivedSessionId ? receivedSessionId.substring(0, 20) + '...' : 'none'}`);

          if (receivedToken) {
            // Save to localStorage
            localStorage.setItem('clerk_token', receivedToken);
            if (receivedSessionId) {
              localStorage.setItem('clerk_session_id', receivedSessionId);
            }

            // Update state
            setToken(receivedToken);
            setSessionId(receivedSessionId);
            setIsSignedIn(true);
            log('Sign in successful!');
          } else if (receivedSessionId) {
            // Sometimes we only get session ID
            localStorage.setItem('clerk_session_id', receivedSessionId);
            setSessionId(receivedSessionId);
            setIsSignedIn(true);
            log('Sign in with session ID');
          } else {
            log('No token or session in callback');
          }
        } catch (e) {
          log('URL parse error: ' + e.message);
        }
      }
    };

    // Register listener
    CapApp.addListener('appUrlOpen', handleDeepLink);
    log('Deep link listener registered');

    // Check if app was launched with URL
    CapApp.getLaunchUrl().then((result) => {
      if (result?.url) {
        log('App launched with URL');
        handleDeepLink({ url: result.url });
      }
    });
  }, []);

  // Initialize native plugins (StatusBar, Keyboard)
  useEffect(() => {
    const initPlugins = async () => {
      try {
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setOverlaysWebView({ overlay: true });
      } catch (e) {
        console.log('[AppCapacitor] StatusBar init skipped:', e.message);
      }

      try {
        const { Keyboard, KeyboardResize } = await import('@capacitor/keyboard');
        await Keyboard.setResizeMode({ mode: KeyboardResize.None });
        await Keyboard.setAccessoryBarVisible({ isVisible: true });
      } catch (e) {
        console.log('[AppCapacitor] Keyboard init skipped:', e.message);
      }
    };

    initPlugins();
  }, []);

  // Handle sign in button
  const handleSignIn = async () => {
    log('Opening Clerk sign-in...');

    const params = new URLSearchParams({
      redirect_url: WEB_REDIRECT_URI,
      after_sign_in_url: WEB_REDIRECT_URI,
      after_sign_up_url: WEB_REDIRECT_URI,
    });

    const authUrl = `https://${CLERK_ACCOUNT_PORTAL}/sign-in?${params.toString()}`;

    try {
      await Browser.open({ url: authUrl });
      log('Browser opened');
    } catch (err) {
      log('Browser error: ' + err.message);
    }
  };

  // Handle sign out
  const handleSignOut = () => {
    localStorage.removeItem('clerk_token');
    localStorage.removeItem('clerk_session_id');
    setToken(null);
    setSessionId(null);
    setIsSignedIn(false);
    log('Signed out');
  };

  // Not signed in - show sign-in page
  if (!isSignedIn) {
    return <SignInPage onSignIn={handleSignIn} debugLogs={debugLogs} />;
  }

  // Signed in - show full Dashboard
  return (
    <ErrorBoundary>
      <CapacitorAuthProvider
        token={token}
        sessionId={sessionId}
        onSignOut={handleSignOut}
      >
        <ChatManagerProvider>
          <SubscriptionProvider getToken={async () => token} userId={sessionId || 'capacitor-user'}>
            <Router>
              <Routes>
                <Route path="/*" element={<Dashboard />} />
              </Routes>
            </Router>
          </SubscriptionProvider>
        </ChatManagerProvider>
      </CapacitorAuthProvider>
    </ErrorBoundary>
  );
}

export default AppCapacitor;
